import { supabase } from '@/lib/supabase'
import { RECORDINGS_BUCKET } from '@/lib/upload'

/**
 * Save to Photos (Brian, library.html v2.1: "the app saves to Photos; the web
 * downloads it"). Only an old uploaded take needs this: a take recorded now
 * goes to Photos from Record itself (videos live in Photos).
 *
 * The native save is the Record area's `saveVideo(blob, { name, caption })` in
 * `@/lib/nativeMedia`. It is looked up without a static import so this file
 * builds whichever area is committed first.
 */
type SaveVideo = (blob: Blob, o: { name: string; caption?: string }) => Promise<'photos' | 'downloaded' | 'shared' | 'failed'>
const NATIVE = import.meta.glob('../../lib/nativeMedia.ts') as Record<string, () => Promise<{ saveVideo?: SaveVideo }>>

async function nativeSave(): Promise<SaveVideo | null> {
  const load = Object.values(NATIVE)[0]
  if (!load) return null
  try { return (await load()).saveVideo ?? null } catch { return null }
}

/** The newest old upload for a story, signed for an hour, or null. */
async function latestUpload(storyId: string): Promise<{ url: string; mime: string } | null> {
  const { data } = await supabase.from('recordings').select('storage_path, mime_type')
    .eq('story_id', storyId).eq('on_device', false).not('storage_path', 'is', null)
    .order('created_at', { ascending: false }).limit(1).maybeSingle()
  if (!data?.storage_path) return null
  const { data: signed } = await supabase.storage.from(RECORDINGS_BUCKET).createSignedUrl(data.storage_path, 3600)
  return signed?.signedUrl ? { url: signed.signedUrl, mime: data.mime_type } : null
}

/** Saves the story's latest uploaded take to Photos (native) or downloads it (web). */
export async function saveTakeToPhotos(storyId: string, headline: string, caption?: string | null): Promise<boolean> {
  const up = await latestUpload(storyId)
  if (!up) return false
  const name = `gogrov-${headline.slice(0, 40).replace(/[^\w]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || storyId}.${up.mime.includes('mp4') ? 'mp4' : 'webm'}`
  const save = await nativeSave()
  if (save) {
    const blob = await fetch(up.url).then((r) => r.blob())
    return (await save(blob, { name, caption: caption ?? undefined })) !== 'failed'
  }
  const a = document.createElement('a')
  a.href = up.url; a.download = name
  document.body.appendChild(a); a.click(); a.remove()
  return true
}
